/**
 * Conteúdo por segmento do website (cards da home e páginas /para/:segmento)
 *
 * Cada segmento descreve as dores reais do dono do negócio e como o sistema
 * resolve cada uma. Só entram recursos que existem no Vende Aí: por exemplo,
 * a agenda da barbearia usa os pedidos agendados com capacidade por
 * profissional (escala e especialidades); não há escolha de profissional
 * pelo cliente nem lembrete automático antes do horário.
 *
 * Ao criar ou renomear um segmento, atualize também public/sitemap.xml.
 *
 * @author Equipe VendeAi
 * @since 1.2.0
 */

import type { LucideIcon } from 'lucide-react';
import {
  Pizza,
  Sandwich,
  UtensilsCrossed,
  Croissant,
  Scissors,
  Store,
  MessageCircle,
  Split,
  Bike,
  BadgePercent,
  QrCode,
  Tv,
  Layers,
  Printer,
  Wallet,
  ChefHat,
  FileText,
  Users,
  CalendarCheck,
  Package,
  Scale,
  BellRing,
  Gift,
  Repeat,
  ShoppingCart,
  Receipt,
  Star,
  Timer,
} from 'lucide-react';

export interface SegmentPain {
  title: string;
  description: string;
}

export interface SegmentSolution {
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface SegmentMockup {
  storeName: string;
  emoji: string;
  status: string;
  categories: string[];
  items: { name: string; desc: string; price: string; emoji: string }[];
  cartLabel: string;
  cartTotal: string;
}

export interface SegmentPage {
  /** Valor de TenantType usado no cadastro (?segmento=) */
  type: string;
  /** Caminho da página: /para/:slug */
  slug: string;
  label: string;
  /** Nome no plural usado nos textos ("pizzarias") */
  audience: string;
  icon: LucideIcon;
  /** Frase curta do card da home */
  description: string;
  highlights: string[];
  seoTitle: string;
  seoDescription: string;
  heroEyebrow: string;
  heroTitle: string;
  heroHighlight: string;
  heroSubtitle: string;
  pains: SegmentPain[];
  solutions: SegmentSolution[];
  /** Passo a passo específico do segmento (ex.: agenda da barbearia) */
  howItWorks?: { title: string; description: string }[];
  /** Mostra a calculadora de comissão (só faz sentido para quem vende em marketplace) */
  showSavings: boolean;
  mockup: SegmentMockup;
  faq: { question: string; answer: string }[];
}

export const SEGMENT_PAGES: SegmentPage[] = [
  {
    type: 'PIZZERIA',
    slug: 'pizzaria',
    label: 'Pizzaria',
    audience: 'pizzarias',
    icon: Pizza,
    description:
      'Meio a meio, bordas e tamanhos sem erro, e o WhatsApp de sexta à noite sob controle.',
    highlights: ['Meio a meio com regra de preço', 'Robô no WhatsApp', 'Taxa por bairro'],
    seoTitle: 'Sistema para pizzaria com cardápio digital e robô de WhatsApp | Vende Aí',
    seoDescription:
      'Cardápio digital para pizzaria com meio a meio, bordas e preço por tamanho, robô que fecha pedidos no WhatsApp, entregas por bairro e PIX confirmado no seu banco. Sem comissão por pedido. Teste grátis por 30 dias.',
    heroEyebrow: 'Para pizzarias',
    heroTitle: 'Sexta à noite sem pedido perdido e',
    heroHighlight: 'sem meio a meio errado',
    heroSubtitle:
      'O cliente monta a pizza sozinho, com sabores, borda e tamanho, pelo link ou pelo robô do WhatsApp. O pedido chega completo na cozinha, o PIX é confirmado direto no seu banco e você não paga comissão.',
    pains: [
      {
        title: 'WhatsApp explodindo no pico',
        description:
          'Áudio para ouvir, cliente perguntando o preço, atendente anotando na pressa e pedido esquecido no meio da conversa.',
      },
      {
        title: 'Meio a meio calculado errado',
        description:
          'Cada atendente cobra de um jeito, a borda fica de fora e o sabor sai trocado. É prejuízo e cliente reclamando.',
      },
      {
        title: 'Comissão levando a margem',
        description:
          'No marketplace, parte de cada pizza fica com o aplicativo, e o cliente que você conquistou não é seu.',
      },
      {
        title: 'Entrega sem controle',
        description:
          'Taxa diferente por bairro na cabeça do atendente e nenhuma visão de qual entregador levou qual pedido.',
      },
    ],
    solutions: [
      {
        icon: Split,
        title: 'Meio a meio do jeito certo',
        description:
          'Limite de sabores por tamanho e preço pelo sabor mais caro ou pela média. Bordas e adicionais já somados.',
      },
      {
        icon: MessageCircle,
        title: 'Robô que fecha o pedido no WhatsApp',
        description:
          'Mostra o cardápio, monta o carrinho, aplica cupom, pede endereço e forma de pagamento. Sem atendente.',
      },
      {
        icon: Bike,
        title: 'Entregas e entregadores',
        description:
          'Taxa por região, atribuição de entregadores e status de cada entrega no painel.',
      },
      {
        icon: Printer,
        title: 'Pedido completo na cozinha',
        description:
          'Alerta sonoro, impressão do pedido e painel em tempo real. Nada de comanda ilegível.',
      },
      {
        icon: Wallet,
        title: 'PIX confirmado no seu banco',
        description:
          'O pagamento é conferido direto no banco ou gateway. Acabou a conferência de comprovante no meio do movimento.',
      },
      {
        icon: Gift,
        title: 'Fidelidade e cupons',
        description:
          'Pontos, cashback, pizza grátis como recompensa e cupons para trazer o cliente de volta no fim de semana.',
      },
    ],
    showSavings: true,
    mockup: {
      storeName: 'Pizzaria Bella Massa',
      emoji: '🍕',
      status: 'Aberto · 30-45 min',
      categories: ['Pizzas', 'Doces', 'Bebidas', 'Combos'],
      items: [
        {
          name: 'Pizza Calabresa',
          desc: 'Molho, mussarela, calabresa',
          price: 'R$ 54,90',
          emoji: '🍕',
        },
        { name: 'Meio a meio', desc: 'Escolha 2 sabores', price: 'R$ 62,90', emoji: '🍕' },
        { name: 'Borda catupiry', desc: 'Adicional', price: 'R$ 9,90', emoji: '🧀' },
        { name: 'Refrigerante 2L', desc: 'Gelado', price: 'R$ 14,00', emoji: '🥤' },
      ],
      cartLabel: 'Ver carrinho · 2 itens',
      cartTotal: 'R$ 117,80',
    },
    faq: [
      {
        question: 'Consigo cobrar o meio a meio pelo sabor mais caro?',
        answer:
          'Sim. Em cada tamanho você define quantos sabores o cliente pode escolher e se o preço segue o sabor mais caro ou a média dos sabores.',
      },
      {
        question: 'O robô do WhatsApp entende pizza meio a meio?',
        answer:
          'O robô segue o mesmo cardápio da loja online, por opções numeradas. Para pedidos com várias combinações, ele pode enviar o link do cardápio, onde o cliente monta a pizza com todas as opções.',
      },
    ],
  },
  {
    type: 'SNACK_BAR',
    slug: 'hamburgueria',
    label: 'Lanchonete e hamburgueria',
    audience: 'lanchonetes e hamburguerias',
    icon: Sandwich,
    description: 'Adicionais cobrados certo, combos que vendem mais e fila no balcão organizada.',
    highlights: ['Adicionais pagos', 'Combos', 'Painel de fila na TV'],
    seoTitle: 'Sistema para hamburgueria e lanchonete com cardápio digital | Vende Aí',
    seoDescription:
      'Cardápio digital para hamburgueria e lanchonete com adicionais pagos, combos, painel de fila na TV, robô de WhatsApp e PIX confirmado no seu banco. Sem comissão por pedido. Teste grátis por 30 dias.',
    heroEyebrow: 'Para lanchonetes e hamburguerias',
    heroTitle: 'Cada adicional cobrado,',
    heroHighlight: 'cada pedido no tempo certo',
    heroSubtitle:
      'O cliente escolhe o ponto, os adicionais e o combo sozinho. A cozinha recebe tudo certinho, a TV mostra quem está pronto para retirar e você vende pelo seu canal, sem taxa por pedido.',
    pains: [
      {
        title: 'Bacon extra que ninguém cobrou',
        description:
          'Adicional anotado no papel e esquecido no caixa. Parece pouco, mas no fim do mês vira muito dinheiro.',
      },
      {
        title: 'Fila e gritaria no balcão',
        description:
          'Cliente perguntando se o pedido já saiu e atendente parando a produção para responder.',
      },
      {
        title: 'Ticket médio parado',
        description:
          'Sem combos e sugestões na hora do pedido, o cliente leva só o lanche e vai embora.',
      },
      {
        title: 'Cliente que compra uma vez e some',
        description:
          'Sem cadastro nem histórico, não tem como chamar de volta quem não pede há semanas.',
      },
    ],
    solutions: [
      {
        icon: Layers,
        title: 'Adicionais e combos',
        description:
          'Complementos obrigatórios e opcionais com preço e combos que aumentam o ticket médio.',
      },
      {
        icon: Tv,
        title: 'Painel de fila na TV',
        description:
          'Tela com os pedidos em preparo e prontos para retirada. O cliente acompanha sem perguntar no balcão.',
      },
      {
        icon: Receipt,
        title: 'Balcão, telefone e delivery juntos',
        description:
          'Lance pedidos do balcão e do telefone no painel, junto com os do link, do WhatsApp e da mesa.',
      },
      {
        icon: MessageCircle,
        title: 'Robô e avisos no WhatsApp',
        description:
          'O robô fecha pedidos e o cliente recebe cada mudança de status: confirmado, em preparo, saiu para entrega.',
      },
      {
        icon: ShoppingCart,
        title: 'Carrinho abandonado',
        description: 'Veja quem desistiu no meio do pedido e recupere a venda com uma mensagem.',
      },
      {
        icon: Repeat,
        title: 'Campanhas automáticas',
        description:
          'Aniversário, boas-vindas, pós-venda e reativação de clientes inativos, sem fazer nada manualmente.',
      },
    ],
    showSavings: true,
    mockup: {
      storeName: 'Smash House',
      emoji: '🍔',
      status: 'Aberto · 20-35 min',
      categories: ['Burgers', 'Combos', 'Porções', 'Bebidas'],
      items: [
        {
          name: 'Smash Duplo',
          desc: 'Pão brioche, 2 carnes, cheddar',
          price: 'R$ 32,90',
          emoji: '🍔',
        },
        { name: 'Combo Smash', desc: 'Burger + batata + refri', price: 'R$ 44,90', emoji: '🍟' },
        { name: 'Bacon extra', desc: 'Adicional', price: 'R$ 6,00', emoji: '🥓' },
        { name: 'Milkshake', desc: 'Chocolate ou morango', price: 'R$ 18,90', emoji: '🥤' },
      ],
      cartLabel: 'Ver carrinho · 3 itens',
      cartTotal: 'R$ 83,80',
    },
    faq: [
      {
        question: 'Consigo lançar pedidos do balcão no mesmo sistema?',
        answer:
          'Sim. Pelo botão "Novo pedido" do painel você registra pedidos do balcão e do telefone, que entram na mesma fila dos pedidos online, da mesa e do WhatsApp.',
      },
      {
        question: 'Preciso de uma TV especial para o painel de fila?',
        answer:
          'Não. O painel abre no navegador de qualquer TV, computador ou tablet ligado à internet.',
      },
    ],
  },
  {
    type: 'RESTAURANT',
    slug: 'restaurante',
    label: 'Restaurante',
    audience: 'restaurantes',
    icon: UtensilsCrossed,
    description:
      'Salão com pedido e pagamento pelo QR Code da mesa, delivery e custo real dos pratos.',
    highlights: ['Pedido e conta pela mesa', 'Ficha técnica', 'Nota fiscal'],
    seoTitle: 'Sistema para restaurante com QR Code na mesa e delivery | Vende Aí',
    seoDescription:
      'Sistema para restaurante com pedido e pagamento pelo QR Code da mesa, delivery próprio, ficha técnica, estoque, financeiro e NFC-e. Sem comissão por pedido. Teste grátis por 30 dias.',
    heroEyebrow: 'Para restaurantes',
    heroTitle: 'Salão girando mais rápido e',
    heroHighlight: 'o custo de cada prato na mão',
    heroSubtitle:
      'O cliente pede e paga pelo QR Code da mesa, a cozinha recebe na hora e o estoque baixa sozinho pela ficha técnica. Salão e delivery no mesmo sistema, com financeiro e nota fiscal.',
    pains: [
      {
        title: 'Mesa esperando o garçom',
        description:
          'Para pedir, para pedir mais uma bebida e para pagar. Mesa parada é faturamento que não entra.',
      },
      {
        title: 'Comanda de papel errada',
        description:
          'Pedido que não chegou na cozinha, prato repetido na conta e discussão na hora de fechar.',
      },
      {
        title: 'Conta dividida que vira confusão',
        description:
          'Cada um paga uma parte, alguém vai embora antes e o caixa não bate no fim da noite.',
      },
      {
        title: 'Não saber o lucro de cada prato',
        description:
          'Sem ficha técnica, o preço é chute, o desperdício passa despercebido e o insumo acaba sem aviso.',
      },
    ],
    solutions: [
      {
        icon: QrCode,
        title: 'Pedido pela mesa',
        description:
          'Cada mesa tem um QR Code. O cliente abre o cardápio no celular, sem baixar aplicativo, e pede quando quiser.',
      },
      {
        icon: Wallet,
        title: 'Conta paga pelo celular',
        description:
          'PIX direto da mesa, com pagamento parcial entre as pessoas e liberação automática da mesa.',
      },
      {
        icon: ChefHat,
        title: 'Cozinha recebe na hora',
        description:
          'Alerta sonoro, impressão e painel em tempo real. Nada se perde entre o salão e a cozinha.',
      },
      {
        icon: Package,
        title: 'Ficha técnica e estoque',
        description:
          'Cada venda baixa os ingredientes do estoque. Você vê o custo dos pratos e recebe alerta de insumo acabando.',
      },
      {
        icon: FileText,
        title: 'Financeiro e nota fiscal',
        description:
          'Contas a pagar e a receber, conciliação bancária e emissão de NF-e e NFC-e integrada à SEFAZ.',
      },
      {
        icon: Users,
        title: 'Equipe organizada',
        description: 'Usuários por função, escalas e ponto eletrônico da equipe no mesmo lugar.',
      },
    ],
    showSavings: true,
    mockup: {
      storeName: 'Cantina do Porto',
      emoji: '🍝',
      status: 'Mesa 12 · pedido pelo QR Code',
      categories: ['Entradas', 'Massas', 'Carnes', 'Vinhos'],
      items: [
        { name: 'Bruschetta', desc: 'Tomate, manjericão e azeite', price: 'R$ 29,90', emoji: '🥖' },
        { name: 'Fettuccine', desc: 'Molho de cogumelos', price: 'R$ 58,90', emoji: '🍝' },
        { name: 'Filé ao molho', desc: 'Com risoto de parmesão', price: 'R$ 79,90', emoji: '🥩' },
        { name: 'Taça de vinho', desc: 'Tinto da casa', price: 'R$ 24,00', emoji: '🍷' },
      ],
      cartLabel: 'Enviar pedido da mesa',
      cartTotal: 'R$ 138,80',
    },
    faq: [
      {
        question: 'O cliente precisa baixar algum aplicativo para pedir pela mesa?',
        answer:
          'Não. Ele lê o QR Code com a câmera do celular e o cardápio abre no navegador. O pedido cai na hora no painel e na cozinha.',
      },
      {
        question: 'Dá para dividir a conta entre as pessoas da mesa?',
        answer:
          'Sim. A conta pode ser paga em partes pelo celular, e a mesa é liberada automaticamente quando o total é quitado.',
      },
    ],
  },
  {
    type: 'BAKERY',
    slug: 'padaria',
    label: 'Padaria e confeitaria',
    audience: 'padarias e confeitarias',
    icon: Croissant,
    description:
      'Encomendas com data e horário, limite de produção por dia e insumos sob controle.',
    highlights: ['Encomendas agendadas', 'Preço por quantidade', 'Estoque de insumos'],
    seoTitle: 'Sistema para padaria e confeitaria com encomendas online | Vende Aí',
    seoDescription:
      'Sistema para padaria e confeitaria com encomendas agendadas, limite de produção por dia, pagamento antecipado por PIX, preço por quantidade e controle de insumos. Teste grátis por 30 dias.',
    heroEyebrow: 'Para padarias e confeitarias',
    heroTitle: 'Encomendas organizadas,',
    heroHighlight: 'sem caderno e sem esquecimento',
    heroSubtitle:
      'O cliente escolhe o bolo, a data e o horário de retirada ou entrega, paga antecipado e você recebe tudo no painel, com limite de encomendas por dia para não aceitar mais do que consegue produzir.',
    pains: [
      {
        title: 'Encomenda anotada no caderno',
        description:
          'O bolo de aniversário esquecido, a data trocada e o cliente chegando para buscar algo que não foi feito.',
      },
      {
        title: 'Aceitar mais do que consegue produzir',
        description:
          'Sem visão da agenda, o sábado lota de encomendas e a equipe vira a madrugada.',
      },
      {
        title: 'Cliente que desiste e não paga',
        description: 'Produção feita, cliente que não aparece e prejuízo com o insumo usado.',
      },
      {
        title: 'Insumo acabando no meio da produção',
        description:
          'Farinha, chocolate e embalagem controlados de cabeça até faltar na hora errada.',
      },
    ],
    solutions: [
      {
        icon: CalendarCheck,
        title: 'Encomendas com data e horário',
        description:
          'O cliente agenda retirada ou entrega pelo link, com antecedência mínima e prazo máximo que você define.',
      },
      {
        icon: Timer,
        title: 'Limite de produção',
        description:
          'Limite por dia, por horário ou pelo tempo de preparo e pela escala da equipe. A loja para de aceitar quando lota.',
      },
      {
        icon: Wallet,
        title: 'Pagamento antecipado',
        description:
          'PIX ou cartão online no momento da encomenda, com confirmação automática no seu banco.',
      },
      {
        icon: Scale,
        title: 'Preço por quantidade',
        description:
          'Cento de salgados, docinhos e kits festa com preço que diminui conforme a quantidade.',
      },
      {
        icon: Package,
        title: 'Insumos e ficha técnica',
        description:
          'Baixa automática dos ingredientes a cada venda e alerta de estoque baixo antes de faltar.',
      },
      {
        icon: BellRing,
        title: 'Confirmação pelo WhatsApp',
        description:
          'O cliente recebe a confirmação da encomenda e qualquer remarcação de horário.',
      },
    ],
    howItWorks: [
      {
        title: 'Você define a agenda',
        description:
          'Dias e horários de produção, antecedência mínima e o limite de encomendas por dia ou por horário.',
      },
      {
        title: 'O cliente encomenda pelo link',
        description:
          'Escolhe os produtos, a data e um horário disponível, e paga antecipado por PIX ou cartão.',
      },
      {
        title: 'Tudo aparece no painel',
        description:
          'Encomendas do dia separadas por horário, com aviso pelo WhatsApp ao confirmar ou remarcar.',
      },
    ],
    showSavings: false,
    mockup: {
      storeName: 'Doce Fornada',
      emoji: '🎂',
      status: 'Encomendas para amanhã · 14h',
      categories: ['Bolos', 'Doces', 'Salgados', 'Pães'],
      items: [
        { name: 'Bolo de brigadeiro', desc: '2 kg · encomenda', price: 'R$ 139,90', emoji: '🎂' },
        { name: 'Cento de coxinha', desc: 'Preço por quantidade', price: 'R$ 89,90', emoji: '🥟' },
        { name: 'Brigadeiro gourmet', desc: 'Caixa com 20', price: 'R$ 48,00', emoji: '🍫' },
        { name: 'Pão de fermentação natural', desc: 'Sai às 7h', price: 'R$ 22,00', emoji: '🥖' },
      ],
      cartLabel: 'Agendar para sáb · 14h',
      cartTotal: 'R$ 229,80',
    },
    faq: [
      {
        question: 'Consigo limitar quantas encomendas aceito por dia?',
        answer:
          'Sim. Você escolhe o limite por dia, por dia e horário, ou pela capacidade de produção calculada pelo tempo de preparo e pela escala da equipe. Quando o limite é atingido, o horário deixa de aparecer para o cliente.',
      },
      {
        question: 'Posso vender o cento de salgados com preço diferente?',
        answer:
          'Sim. O preço por quantidade aplica valores menores conforme o cliente leva mais unidades.',
      },
    ],
  },
  {
    type: 'BARBERSHOP',
    slug: 'barbearia',
    label: 'Barbearia',
    audience: 'barbearias',
    icon: Scissors,
    description: 'Agenda online com horários livres pela escala dos barbeiros e venda de produtos.',
    highlights: ['Agenda online', 'Escala dos barbeiros', 'Venda de produtos'],
    seoTitle: 'Sistema para barbearia com agenda online e venda de produtos | Vende Aí',
    seoDescription:
      'Agenda online para barbearia: o cliente escolhe o serviço, o dia e um horário livre calculado pela escala dos barbeiros. Pagamento antecipado, venda de produtos, fidelidade e campanhas. Teste grátis por 30 dias.',
    heroEyebrow: 'Para barbearias',
    heroTitle: 'Agenda cheia e organizada,',
    heroHighlight: 'sem viver no WhatsApp',
    heroSubtitle:
      'O cliente escolhe o serviço, o dia e um horário livre pelo link da barbearia. Os horários seguem a escala e as especialidades de cada barbeiro, e a confirmação chega pelo WhatsApp.',
    pains: [
      {
        title: 'Agenda no caderno e no WhatsApp',
        description:
          'Entre um corte e outro, parar para responder "tem horário hoje?" e anotar à mão.',
      },
      {
        title: 'Dois clientes no mesmo horário',
        description:
          'Encaixe feito de cabeça que vira espera, cliente irritado e barbeiro sobrecarregado.',
      },
      {
        title: 'Cliente que marca e não aparece',
        description: 'Horário bloqueado, cadeira vazia e dinheiro que não volta.',
      },
      {
        title: 'Cliente que some depois do corte',
        description:
          'Sem histórico nem contato organizado, não tem como lembrar quem não volta há semanas.',
      },
    ],
    solutions: [
      {
        icon: CalendarCheck,
        title: 'Agenda online pelo link',
        description:
          'O cliente escolhe o serviço, o dia e um horário disponível a qualquer hora, sem precisar chamar ninguém.',
      },
      {
        icon: Users,
        title: 'Horários pela escala da equipe',
        description:
          'Cada barbeiro tem dias, horários, almoço e especialidades. O sistema só oferece o que a equipe consegue atender.',
      },
      {
        icon: Wallet,
        title: 'Pagamento antecipado',
        description:
          'Cobre na hora do agendamento por PIX ou cartão online. Quem paga antes costuma faltar menos.',
      },
      {
        icon: Store,
        title: 'Venda de produtos',
        description:
          'Pomadas, óleos e cosméticos na mesma loja online, com retirada na barbearia ou entrega.',
      },
      {
        icon: Star,
        title: 'Fidelidade e cashback',
        description:
          'Pontos a cada visita e recompensas como um corte grátis ou desconto no próximo serviço.',
      },
      {
        icon: Repeat,
        title: 'Campanhas de retorno',
        description:
          'Mensagens de aniversário, boas-vindas e reativação de clientes que não voltam há um tempo.',
      },
    ],
    howItWorks: [
      {
        title: 'Cadastre serviços e equipe',
        description:
          'Serviços com duração e preço, e cada barbeiro com dias de trabalho, horário de almoço e especialidades.',
      },
      {
        title: 'O cliente agenda pelo link',
        description:
          'Escolhe o serviço, o dia e um horário livre, e pode pagar antecipado por PIX ou cartão.',
      },
      {
        title: 'Confirmação pelo WhatsApp',
        description:
          'O cliente recebe a confirmação e qualquer remarcação. Você vê a agenda do dia no painel.',
      },
    ],
    showSavings: false,
    mockup: {
      storeName: 'Barbearia Navalha',
      emoji: '💈',
      status: 'Horários livres hoje: 15h, 16h30',
      categories: ['Cortes', 'Barba', 'Combos', 'Produtos'],
      items: [
        { name: 'Corte masculino', desc: '40 min', price: 'R$ 45,00', emoji: '✂️' },
        { name: 'Barba completa', desc: '30 min · toalha quente', price: 'R$ 35,00', emoji: '🧔' },
        { name: 'Corte + barba', desc: '1h10', price: 'R$ 70,00', emoji: '💈' },
        { name: 'Pomada modeladora', desc: 'Produto', price: 'R$ 39,90', emoji: '🫙' },
      ],
      cartLabel: 'Agendar hoje · 16h30',
      cartTotal: 'R$ 70,00',
    },
    faq: [
      {
        question: 'O cliente consegue escolher o barbeiro?',
        answer:
          'Hoje o cliente escolhe o serviço, o dia e o horário. O sistema oferece apenas os horários em que há barbeiro disponível com aquela especialidade, de acordo com a escala da equipe.',
      },
      {
        question: 'O cliente recebe confirmação do horário?',
        answer:
          'Sim. Com o WhatsApp conectado, o cliente recebe a confirmação do agendamento e o aviso de qualquer remarcação.',
      },
    ],
  },
  {
    type: 'OTHER',
    slug: 'outros-negocios',
    label: 'Açaí, marmitaria e outros',
    audience: 'açaiterias, marmitarias, empórios e outros negócios',
    icon: Store,
    description: 'Açaí, marmitaria, sorveteria, empório, adega e doceria com loja online própria.',
    highlights: ['Monte seu pedido', 'Pedidos agendados', 'Preço por quantidade'],
    seoTitle: 'Cardápio digital e loja online para açaí, marmitaria, adega e empório | Vende Aí',
    seoDescription:
      'Loja online própria para açaiteria, marmitaria, sorveteria, empório, adega e doceria, com complementos, pedidos agendados, preço por quantidade, robô de WhatsApp e PIX confirmado no seu banco. Teste grátis por 30 dias.',
    heroEyebrow: 'Para açaí, marmitaria, adega, empório e mais',
    heroTitle: 'Sua loja online pronta,',
    heroHighlight: 'do seu jeito de vender',
    heroSubtitle:
      'Monte seu açaí com complementos, marmitas agendadas para a semana, kits com preço por quantidade ou entrega de bebidas. Um catálogo flexível, com robô no WhatsApp e pagamento online sem comissão.',
    pains: [
      {
        title: 'Montar o pedido pelo WhatsApp',
        description:
          'Complemento por complemento, trocando mensagens enquanto a fila cresce e outros clientes esperam.',
      },
      {
        title: 'Cardápio em foto que ninguém atualiza',
        description: 'Preço desatualizado, item que acabou e cliente pedindo o que não tem.',
      },
      {
        title: 'Comissão alta em pedido pequeno',
        description:
          'Em pedido de ticket baixo, a comissão do marketplace pesa ainda mais na margem.',
      },
      {
        title: 'Pedido para outro dia sem controle',
        description: 'Marmitas da semana e encomendas anotadas em lugares diferentes.',
      },
    ],
    solutions: [
      {
        icon: Layers,
        title: 'Monte seu pedido',
        description:
          'Tamanhos, complementos obrigatórios e opcionais, limites de escolha e preço calculado sozinho.',
      },
      {
        icon: MessageCircle,
        title: 'Robô no WhatsApp',
        description:
          'Atende, mostra o cardápio e fecha o pedido pela conversa enquanto você prepara.',
      },
      {
        icon: CalendarCheck,
        title: 'Pedidos agendados',
        description: 'Marmitas da semana e encomendas com data, horário e limite por dia.',
      },
      {
        icon: Scale,
        title: 'Preço por quantidade',
        description: 'Kits, caixas e fardos com preço menor para quem leva mais.',
      },
      {
        icon: BadgePercent,
        title: 'Promoções na vitrine',
        description:
          'Barra de anúncio, banner de promoção relâmpago e pop-up de primeira compra na sua loja.',
      },
      {
        icon: Wallet,
        title: 'PIX e cartão online',
        description: 'Pagamento confirmado direto no seu banco ou gateway, sem comissão para nós.',
      },
    ],
    showSavings: true,
    mockup: {
      storeName: 'Açaí da Praça',
      emoji: '🍇',
      status: 'Aberto · 15-25 min',
      categories: ['Açaí', 'Complementos', 'Marmitas', 'Bebidas'],
      items: [
        { name: 'Açaí 500 ml', desc: 'Escolha até 4 complementos', price: 'R$ 22,90', emoji: '🍇' },
        { name: 'Leite em pó', desc: 'Complemento', price: 'R$ 2,50', emoji: '🥛' },
        { name: 'Marmita fit', desc: 'Frango e legumes', price: 'R$ 24,90', emoji: '🍱' },
        { name: 'Kit 6 cervejas', desc: 'Preço por quantidade', price: 'R$ 39,90', emoji: '🍺' },
      ],
      cartLabel: 'Ver carrinho · 2 itens',
      cartTotal: 'R$ 47,80',
    },
    faq: [
      {
        question: 'Meu tipo de negócio não está na lista. Funciona para mim?',
        answer:
          'Se você vende produtos ou serviços com catálogo, pedido e pagamento, provavelmente sim. No cadastro escolha "Outros negócios" e fale com o nosso time se quiser ajuda para configurar.',
      },
      {
        question: 'Consigo limitar quantos complementos o cliente escolhe?',
        answer:
          'Sim. Cada grupo de complementos tem mínimo e máximo de escolhas, e pode ser obrigatório ou opcional.',
      },
    ],
  },
];

export function findSegmentBySlug(slug: string | undefined): SegmentPage | undefined {
  return SEGMENT_PAGES.find(s => s.slug === slug);
}
